import { create } from 'zustand';
import { layout, type Placement, type Sized } from './masonry';

/**
 * The grid lives here rather than in component state because the item panel
 * needs tile positions in stage 3 to scroll the selected item into view, and
 * lifting it out of `Board` afterwards would mean rewriting both.
 */

type BoardState = {
  roomId: string | undefined;
  placements: Placement[];
  byId: Map<string, Placement>;
  height: number;
  colW: number;
  cols: number;
  /**
   * False until the first layout pass for this room has painted. Tiles appear
   * in position on mount; they only animate once they've been somewhere.
   */
  laidOut: boolean;
  /** Guards against ResizeObserver firing with a width that hasn't changed. */
  signature: string;

  /**
   * The items on this board, in board order — sections left out. It's what
   * the panel's arrow keys walk, and they walk items: arrowing into a section
   * would open a panel on a record that isn't one.
   */
  sequence: string[];

  measure: (roomId: string, items: readonly Sized[], containerW: number) => void;
  setSequence: (ids: string[]) => void;
  markLaidOut: () => void;
  clear: () => void;
};

const empty = {
  placements: [] as Placement[],
  byId: new Map<string, Placement>(),
  height: 0,
  colW: 0,
  cols: 0,
  laidOut: false,
  signature: '',
  sequence: [] as string[],
};

export const useBoardStore = create<BoardState>((set, get) => ({
  roomId: undefined,
  ...empty,

  measure: (roomId, items, containerW) => {
    if (containerW <= 0) return;

    const signature = `${roomId}:${Math.round(containerW)}:${items
      .map((item) => `${item.id},${item.w}x${item.h}`)
      .join('|')}`;
    const previous = get();
    if (previous.signature === signature) return;

    const next = layout(items, containerW);
    set({
      roomId,
      placements: next.placements,
      byId: next.byId,
      height: next.height,
      colW: next.colW,
      cols: next.cols,
      // A different room is a fresh mount as far as animation is concerned.
      laidOut: previous.roomId === roomId && previous.laidOut,
      signature,
    });
  },

  setSequence: (ids) => {
    const previous = get().sequence;
    const same = previous.length === ids.length && previous.every((id, i) => id === ids[i]);
    if (!same) set({ sequence: ids });
  },

  markLaidOut: () => {
    if (!get().laidOut) set({ laidOut: true });
  },

  clear: () => set({ roomId: undefined, ...empty }),
}));

/** Stage 3's way in: where is this tile, right now. */
export function placementOf(id: string): Placement | undefined {
  return useBoardStore.getState().byId.get(id);
}
